import { useState, useEffect, useRef, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "framer-motion";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import { Button } from "./ui/button";

import {
  LayoutDashboard,
  ArrowUp,
  Loader2,
  Check,
  X,
} from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useTheme } from "@/providers/ThemeProvider";
import { hapticChargeExplode, hapticSuccess } from "@/utils/haptics";
import { useQueryClient } from "@tanstack/react-query";
import { SandboxedView } from "./SandboxedView";
import {
  useSaveCustomView,
  useUpdateCustomView,
} from "@/hooks/useCustomViews";

// ─── Types ──────────────────────────────────────────────────
interface ChatMessage {
  role: "assistant" | "user";
  content: string;
  toolsUsed?: string[] | null;
  /** If the assistant emitted a view, store the HTML here */
  viewHtml?: string | null;
  viewTitle?: string | null;
  viewDescription?: string | null;
}

type StatusLabel =
  | "Thinking..."
  | "Designing..."
  | "Fetching your data..."
  | "Creating component..."
  | "Fixing crash..."
  | "Refining widget..."
  | "Analyzing your data..."
  | " Loading your metrics"

  | "📋 Retrieving entry logs"
  | "📋 Checking rest days"
  | "🎨 Building widget"
  | "↔️ Adjusting widget size";

const toolDescriptions: Record<string, string> = {
  get_user_metrics: "📈 Loading your metrics",
  get_detailed_entries: "📋 Retrieving entry logs",
  get_no_data_entries: "📋 Checking rest days",
  emit_view: "🎨 Building widget",
  change_widget_size: "↔️ Adjusting widget size",
};

// ─── Props ──────────────────────────────────────────────────
interface CustomViewCreatorDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  userId: string;
  initialPrompt?: string;
  userCredits?: number;
  /** For refining an existing view */
  existingViewId?: string | null;
  existingViewHtml?: string | null;
  existingViewTitle?: string | null;
  existingViewSize?: 'full' | 'half' | null;
  existingViewExplanation?: string | null;
}

export const CustomViewCreatorDialog = ({
  open,
  onOpenChange,
  userId,
  initialPrompt = "",
  userCredits,
  existingViewId = null,
  existingViewHtml = null,
  existingViewTitle = null,
  existingViewSize = null,
  existingViewExplanation = null,
}: CustomViewCreatorDialogProps) => {
  const { t } = useTranslation();
  const { theme } = useTheme();
  const queryClient = useQueryClient();

  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [inputValue, setInputValue] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [statusLabel, setStatusLabel] = useState<StatusLabel>("Thinking...");
  const [hasInitialPrompt, setHasInitialPrompt] = useState(false);

  // Widget size selection (shown before chat in create mode)
  const [selectedSize, setSelectedSize] = useState<'full' | 'half' | null>(null);

  // Live tool tracking (streamed from backend)
  const [liveTools, setLiveTools] = useState<string[]>([]);

  // Fake progress bar state
  const [progress, setProgress] = useState(0);
  const [showLoading, setShowLoading] = useState(false);
  const progressTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const progressStartRef = useRef<number>(0);

  // Start / stop the fake progress bar
  useEffect(() => {
    if (isLoading) {
      // Reset and start
      setProgress(0);
      setShowLoading(true);
      progressStartRef.current = Date.now();

      // Tick every 200ms using an ease-out curve over 120s (2 min)
      // Formula: progress = 95 * (1 - e^(-t/40))   (asymptotically approaches 95%)
      progressTimerRef.current = setInterval(() => {
        const elapsed = (Date.now() - progressStartRef.current) / 1000; // seconds
        const value = 95 * (1 - Math.exp(-elapsed / 40));
        setProgress(Math.min(value, 95));
      }, 200);
    } else if (showLoading) {
      // Loading just finished — clear the tick timer and hide immediately
      // (no delay — prevents skeleton + widget both being in DOM, which
      //  inflates scrollHeight and causes the scroll-overshoot bug)
      if (progressTimerRef.current) {
        clearInterval(progressTimerRef.current);
        progressTimerRef.current = null;
      }
      setShowLoading(false);
    }

    return () => {
      if (progressTimerRef.current) {
        clearInterval(progressTimerRef.current);
        progressTimerRef.current = null;
      }
    };
  }, [isLoading]);

  // Track the latest emitted view for accept/reject
  const [pendingHtml, setPendingHtml] = useState<string | null>(null);
  const [pendingTitle, setPendingTitle] = useState<string | null>(null);
  const [pendingDescription, setPendingDescription] = useState<string | null>(
    null
  );
  const [pendingExplanation, setPendingExplanation] = useState<string | null>(
    null
  );
  const [accepted, setAccepted] = useState(false);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  // Auto-resize textarea (grows upward since it's at the bottom of the dialog)
  const autoResizeInput = useCallback(() => {
    const el = inputRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, 120)}px`;
  }, []);
  const scrollIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const saveView = useSaveCustomView(userId);
  const updateView = useUpdateCustomView(userId);

  const isRefineMode = !!existingViewId;

  // Auto-submit initial prompt (only after size is selected)
  useEffect(() => {
    if (open && messages.length === 0 && initialPrompt && !hasInitialPrompt && (selectedSize || isRefineMode)) {
      setHasInitialPrompt(true);
      setTimeout(() => submitMessage(initialPrompt), 500);
    }
  }, [open, initialPrompt, selectedSize]);

  // Reset on close
  useEffect(() => {
    if (!open) {
      setMessages([]);
      setInputValue("");
      setIsLoading(false);
      setHasInitialPrompt(false);
      setPendingHtml(null);
      setPendingTitle(null);
      setPendingDescription(null);
      setPendingExplanation(null);
      setAccepted(false);
      setLiveTools([]);
      setSelectedSize(null);
    }
  }, [open]);

  // In refine mode, inherit the existing size and seed a greeting message
  useEffect(() => {
    if (open && isRefineMode && existingViewSize) {
      setSelectedSize(existingViewSize);
    }
    if (open && isRefineMode && messages.length === 0) {
      setMessages([
        {
          role: "assistant",
          content: `What would you like to change about your **${existingViewTitle || "widget"}**?`,
        },
      ]);
    }
  }, [open, isRefineMode, existingViewSize]);

  // Smooth-scroll helper
  const scrollToBottom = useCallback(() => {
    const el = scrollContainerRef.current;
    if (!el) return;
    const target = el.scrollHeight;
    // Already at bottom — skip
    if (Math.abs(el.scrollTop + el.clientHeight - target) < 2) return;
    el.scrollTo({ top: target, behavior: "smooth" });
  }, []);

  // Scroll to bottom when messages change
  useEffect(() => {
    // Small RAF delay so the DOM has updated after state changes
    requestAnimationFrame(scrollToBottom);
  }, [messages, scrollToBottom]);

  // Continuously lerp to bottom while actively loading (follows thinking + skeleton as they animate in)
  // Tied to isLoading (not showLoading) so it stops the moment the response arrives,
  // avoiding scroll-overshoot when skeleton and widget briefly coexist in the DOM.
  useEffect(() => {
    if (isLoading) {
      // Immediate scroll, then keep checking every 200ms
      requestAnimationFrame(scrollToBottom);
      scrollIntervalRef.current = setInterval(scrollToBottom, 200);
    } else {
      // Stopped loading — clear interval, then one final scroll after a short
      // delay so the DOM has settled (skeleton removed, widget rendered).
      if (scrollIntervalRef.current) {
        clearInterval(scrollIntervalRef.current);
        scrollIntervalRef.current = null;
      }
      const raf = requestAnimationFrame(() => {
        // Double-RAF ensures layout has been flushed after skeleton removal
        requestAnimationFrame(scrollToBottom);
      });
      return () => cancelAnimationFrame(raf);
    }
    return () => {
      if (scrollIntervalRef.current) {
        clearInterval(scrollIntervalRef.current);
        scrollIntervalRef.current = null;
      }
    };
  }, [isLoading, scrollToBottom]);

  // Also scroll when live tool labels stream in (adds height)
  useEffect(() => {
    if (liveTools.length > 0) {
      requestAnimationFrame(scrollToBottom);
    }
  }, [liveTools, scrollToBottom]);

  // ─── Submit message ─────────────────────────────────────
  const submitMessage = useCallback(
    async (messageText: string) => {
      if (!messageText.trim() || isLoading) return;

      const userMsg = messageText.trim();
      setInputValue("");
      setMessages((prev) => [...prev, { role: "user", content: userMsg }]);
      setIsLoading(true);
      setStatusLabel(
        pendingHtml ? "Refining widget..." : "Thinking..."
      );
      // Reset live tools and pending for new generation
      setLiveTools([]);
      setPendingHtml(null);
      setAccepted(false);

      try {
        const { data: session } = await supabase.auth.getSession();
        if (!session?.session?.access_token) {
          toast.error(t('notifications.toast.please_sign_in', 'Please sign in to continue'));
          setIsLoading(false);
          return;
        }

        const conversationHistory = messages.map((m) => ({
          role: m.role,
          content: m.content,
        }));

        // Use streaming fetch to get real-time tool events
        const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
        const supabaseAnonKey = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;
        const functionUrl = `${supabaseUrl}/functions/v1/create-custom-view`;

        const sseResponse = await fetch(functionUrl, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${session.session.access_token}`,
            apikey: supabaseAnonKey,
            Accept: "text/event-stream",
          },
          body: JSON.stringify({
            userMessage: userMsg,
            conversationHistory,
            timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
            existingHtml: existingViewHtml || pendingHtml || undefined,
            existingExplanation: existingViewExplanation || pendingExplanation || undefined,
            mode: isRefineMode || pendingHtml ? "refine" : "create",
            widgetSize: selectedSize || 'full',
            theme: theme === 'light' ? 'light' : 'dark',
          }),
        });

        // Handle non-streaming error responses
        if (!sseResponse.ok) {
          const errorText = await sseResponse.text();
          let errorData: any = {};
          try { errorData = JSON.parse(errorText); } catch {}
          if (errorData.error?.includes?.("insufficient_credits") || errorText.includes("insufficient_credits")) {
            toast.error(t('notifications.toast.out_of_credits', "You're out of credits"));
            setIsLoading(false);
            return;
          }
          if (errorData.error?.includes?.("premium_required") || errorText.includes("premium_required")) {
            toast.error(t('custom_views.pro_required', 'Custom widgets are a Pro feature'));
            setIsLoading(false);
            return;
          }
          if (errorData.error?.includes?.("max_views_reached") || errorText.includes("max_views_reached")) {
            toast.error(t('custom_views.max_views_reached', 'You have reached the maximum number of widgets. Please delete one before creating a new one.'));
            setIsLoading(false);
            return;
          }
          throw new Error(
            errorData.error || errorData.message || t('custom_views.failed_to_create', 'Failed to create widget')
          );
        }

        // Parse SSE stream
        let resultData: any = null;

        const contentType = sseResponse.headers.get("Content-Type") || "";
        if (contentType.includes("text/event-stream")) {
          const reader = sseResponse.body!.getReader();
          const decoder = new TextDecoder();
          let buffer = "";

          while (true) {
            const { done, value } = await reader.read();
            if (done) break;
            buffer += decoder.decode(value, { stream: true });

            // Process complete SSE lines
            const lines = buffer.split("\n");
            buffer = lines.pop() || ""; // keep incomplete line in buffer

            for (const line of lines) {
              if (!line.startsWith("data: ")) continue;
              try {
                const event = JSON.parse(line.slice(6));
                if (event.type === "tool_start") {
                  setLiveTools((prev) =>
                    prev.includes(event.tool) ? prev : [...prev, event.tool]
                  );
                  // Update status label based on tool
                  const desc = toolDescriptions[event.tool];
                  if (desc) {
                    setStatusLabel(desc as any);
                  }
                  // Yield to let React render & the browser paint so each
                  // tool step is visible for at least 500ms before the next
                  // status update overwrites it.
                  await new Promise((r) => setTimeout(r, 500));
                } else if (event.type === "status") {
                  setStatusLabel(event.label as StatusLabel);
                } else if (event.type === "result") {
                  // Brief pause so users see the final "Building widget" step
                  // before the loading indicator disappears
                  await new Promise((r) => setTimeout(r, 600));
                  resultData = event;
                } else if (event.type === "error") {
                  throw new Error(event.error);
                }
              } catch (parseErr: any) {
                if (parseErr.message && !parseErr.message.includes("JSON")) throw parseErr;
                // Ignore JSON parse errors from partial lines
              }
            }
          }
        } else {
          // Fallback: non-streaming JSON response
          resultData = await sseResponse.json();
        }

        if (!resultData) {
          throw new Error(t('custom_views.failed_to_create', 'Failed to create widget'));
        }

        const { message, html, title, description, toolsUsed } = resultData;

        queryClient.invalidateQueries({
          queryKey: ["user-credits", userId],
        });

        if (html) {
          hapticChargeExplode();
          setPendingHtml(html);
          setPendingTitle(title || "Custom Widget");
          setPendingDescription(description || null);
          setPendingExplanation(message || null);
        }

        setMessages((prev) => [
          ...prev,
          {
            role: "assistant",
            content: message || "Here's your widget!",
            toolsUsed: toolsUsed || null,
            viewHtml: html || null,
            viewTitle: title || null,
            viewDescription: description || null,
          },
        ]);
      } catch (error: any) {
        console.error("[CustomViewCreatorDialog] Error:", error);
        toast.error(t('custom_views.failed_to_create', 'Failed to create widget'));
        setMessages((prev) => prev.slice(0, -1));
        setInputValue(messageText);
      } finally {
        setIsLoading(false);
      }
    },
    [
      isLoading,
      messages,
      pendingHtml,
      pendingExplanation,
      existingViewHtml,
      existingViewExplanation,
      isRefineMode,
      userId,
      queryClient,
    ]
  );

  // ─── Accept view ───────────────────────────────────────
  const handleAccept = async () => {
    if (!pendingHtml || !pendingTitle) return;
    hapticChargeExplode();
    try {
      if (isRefineMode && existingViewId) {
        await updateView.mutateAsync({
          viewId: existingViewId,
          title: pendingTitle,
          description: pendingDescription,
          html_content: pendingHtml,
          explanation: pendingExplanation,
          size: selectedSize || existingViewSize || 'full',
        });
        toast.success(t('custom_views.view_updated', 'Widget updated!') as string);
      } else {
        await saveView.mutateAsync({
          title: pendingTitle,
          description: pendingDescription,
          html_content: pendingHtml,
          explanation: pendingExplanation,
          size: selectedSize || 'full',
        });
        // toast removed — no toast on pin
      }
      setAccepted(true);
      // Close dialog after successful pin/update
      onOpenChange(false);
    } catch (err: any) {
      console.error("[CustomViewCreatorDialog] Save error:", err);
      if (err?.message?.includes("max_views_reached")) {
        toast.error(t('custom_views.max_views_reached', 'You have reached the maximum number of widgets. Please delete one before creating a new one.'));
      } else {
        toast.error(t('custom_views.failed_to_save', 'Failed to save widget'));
      }
    }
  };

  const handleReject = () => {
    setPendingHtml(null);
    setPendingTitle(null);
    setPendingDescription(null);
    setPendingExplanation(null);
    setMessages((prev) => [
      ...prev,
      {
        role: "assistant",
        content:
          "No problem! Tell me what changes you'd like, or describe a completely new widget.",
      },
    ]);
  };

  // ─── Key handler ───────────────────────────────────────
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      submitMessage(inputValue);
    }
  };

  // ─── Render ────────────────────────────────────────────
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-full h-full max-w-full max-h-full rounded-none border-0 flex flex-col" onOpenAutoFocus={(e) => e.preventDefault()}>
        <DialogHeader className="flex-shrink-0 pr-8">
          <DialogTitle className="flex items-center gap-1.5 text-sm">
            <LayoutDashboard className="h-4 w-4 flex-shrink-0 text-primary" />
            {isRefineMode
              ? `Refine: ${existingViewTitle || "Custom Widget"}`
              : "Create Custom Widget"}
          </DialogTitle>
          <span className="self-start rounded-full bg-primary/10 px-1.5 py-0.5 text-[9px] font-medium text-primary leading-none select-none">
            Early Access – Expect bugs
          </span>
        </DialogHeader>

        <>
        {/* Chat Messages Area */}
        <div ref={scrollContainerRef} className="flex-1 overflow-y-auto min-h-0 space-y-4 py-4">
          {/* ─── SIZE PICKER (shown as first in-chat message in create mode) ─── */}
          {!selectedSize && !isRefineMode && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.2 }}
              className="flex flex-col items-start"
            >
              <div className="max-w-[85%] rounded-2xl px-4 py-3 bg-muted space-y-3">
                <p className="text-sm font-semibold">{t('custom_views.choose_widget_size', 'Choose widget size')}</p>
                <p className="text-xs text-muted-foreground">
                  {t('custom_views.size_description', 'This determines how the widget appears on your dashboard')}
                </p>

                <div className="flex flex-col gap-2">
                  {/* Full-width option */}
                  <button
                    onClick={() => setSelectedSize('full')}
                    className="group w-full rounded-xl border-2 border-border/50 bg-card p-3 flex items-center gap-3 transition-all active:scale-[0.98] hover:border-blue-500/60 focus:outline-none focus:border-blue-500/60"
                  >
                    <div className="flex-shrink-0 w-16 h-10 rounded-lg bg-muted/60 overflow-hidden relative">
                      <div className="absolute bottom-1 left-1 right-1 flex items-end gap-0.5 h-[60%]">
                        {[55, 70, 40, 85, 60].map((h, i) => (
                          <div
                            key={i}
                            className="flex-1 rounded-t bg-blue-500/20 animate-pulse"
                            style={{ height: `${h}%`, animationDelay: `${i * 120}ms` }}
                          />
                        ))}
                      </div>
                    </div>
                    <div className="flex-1 text-left">
                      <span className="text-sm font-medium">{t('custom_views.full_width', 'Full Width')}</span>
                      <p className="text-xs text-muted-foreground mt-0.5">{t('custom_views.full_width_desc', 'Charts, tables, detailed views')}</p>
                    </div>
                  </button>

                  {/* Half-width option */}
                  <button
                    onClick={() => setSelectedSize('half')}
                    className="group w-full rounded-xl border-2 border-border/50 bg-card p-3 flex items-center gap-3 transition-all active:scale-[0.98] hover:border-blue-500/60 focus:outline-none focus:border-blue-500/60"
                  >
                    <div className="flex-shrink-0 w-16 h-10 flex gap-1">
                      {[0, 1].map((idx) => (
                        <div key={idx} className="flex-1 rounded-md bg-muted/60 overflow-hidden flex flex-col items-center justify-center gap-0.5">
                          <div className="h-3 w-5 rounded bg-muted-foreground/10 animate-pulse" style={{ animationDelay: `${idx * 200}ms` }} />
                          <div className="h-1 w-4 rounded bg-muted-foreground/8 animate-pulse" style={{ animationDelay: `${idx * 200}ms` }} />
                        </div>
                      ))}
                    </div>
                    <div className="flex-1 text-left">
                      <span className="text-sm font-medium">{t('custom_views.half_width', 'Half Width')}</span>
                      <p className="text-xs text-muted-foreground mt-0.5">{t('custom_views.two_per_row', '2 per row')} · {t('custom_views.half_width_desc', 'Stats, counters, quick glances')}</p>
                    </div>
                  </button>
                </div>
              </div>
            </motion.div>
          )}

          <AnimatePresence initial={false}>
            {messages.map((message, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.2 }}
                className={`flex flex-col ${
                  message.role === "user" ? "items-end" : "items-start"
                }`}
              >
                {/* Tool usage labels */}
                {message.role === "assistant" &&
                  message.toolsUsed &&
                  message.toolsUsed.length > 0 && (
                    <div className="text-xs text-muted-foreground mb-1 px-1 space-y-1">
                      {message.toolsUsed.map((tool, i) => (
                        <div key={i} className="italic">
                          {toolDescriptions[tool] || "⚙️ Processing..."}
                        </div>
                      ))}
                    </div>
                  )}

                {/* Message bubble */}
                <div
                  className={`max-w-[85%] rounded-2xl px-4 py-2.5 ${
                    message.role === "user"
                      ? "bg-blue-500 text-white"
                      : "bg-muted"
                  }`}
                >
                  {message.role === "assistant" ? (
                    <div className="text-sm prose prose-sm dark:prose-invert max-w-none [&>*:first-child]:mt-0 [&>*:last-child]:mb-0 [&_p]:my-1 [&_ul]:my-1 [&_ol]:my-1 [&_li]:my-0.5 [&_pre]:my-2 [&_pre]:rounded-lg [&_pre]:bg-black/10 [&_pre]:dark:bg-white/10 [&_pre]:p-2 [&_code]:text-xs [&_h1]:text-base [&_h2]:text-sm [&_h3]:text-sm [&_table]:text-xs">
                      <ReactMarkdown remarkPlugins={[remarkGfm]}>
                        {message.content}
                      </ReactMarkdown>
                    </div>
                  ) : (
                    <p className="text-sm whitespace-pre-wrap">
                      {message.content}
                    </p>
                  )}
                </div>

                {/* Inline preview of emitted view */}
                {message.role === "assistant" && message.viewHtml && (
                  <div className={`mt-2 ${
                    (selectedSize || existingViewSize) === 'half'
                      ? 'w-[calc(50%-6px)]'
                      : 'w-full'
                  }`}>
                    <SandboxedView
                      htmlContent={message.viewHtml}
                      maxHeight={260}
                      interactive={false}
                      isHalf={(selectedSize || existingViewSize) === 'half'}
                    />
                  </div>
                )}
              </motion.div>
            ))}
          </AnimatePresence>

          {/* Loading indicator with skeleton preview */}
          {showLoading && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              className="flex flex-col items-start gap-3 w-full"
            >
              {/* Live tool labels — streamed in real time */}
              {liveTools.length > 0 && (
                <div className="text-xs text-muted-foreground mb-0 px-1 space-y-1">
                  {liveTools.map((tool, i) => (
                    <motion.div
                      key={tool}
                      initial={{ opacity: 0, x: -8 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.2 }}
                      className="italic flex items-center gap-1.5"
                    >
                      {i < liveTools.length - 1 ? (
                        <Check className="h-3 w-3 text-green-500" />
                      ) : (
                        <Loader2 className="h-3 w-3 animate-spin" />
                      )}
                      {toolDescriptions[tool] || "⚙️ Processing..."}
                    </motion.div>
                  ))}
                </div>
              )}

              {/* Status bubble */}
              <div className="bg-muted rounded-2xl px-4 py-2.5 min-w-[220px]">
                <div className="flex items-center gap-2">
                  {/* 3×3 grid loader — no center square, clockwise */}
                  <div className="grid grid-cols-3 gap-[2px] h-4 w-4 flex-shrink-0">
                    {[0, 1, 2, 3, 4, 5, 6, 7, 8].map((pos) => {
                      if (pos === 4) return <div key={pos} />;
                      // Clockwise order: TL→TC→TR→MR→BR→BC→BL→ML
                      const clockwiseIndex = [0, 1, 2, 5, 8, 7, 6, 3].indexOf(pos);
                      return (
                        <div
                          key={pos}
                          className="rounded-[1px] bg-blue-500 animate-grid-pulse"
                          style={{ animationDelay: `${clockwiseIndex * 125}ms` }}
                        />
                      );
                    })}
                  </div>
                  <span className="text-sm text-muted-foreground">
                    {statusLabel}
                  </span>
                </div>
                {/* Progress bar */}
                <div className="mt-2 h-1.5 w-full rounded-full bg-muted-foreground/15 overflow-hidden">
                  <div
                    className="h-full rounded-full bg-blue-500 transition-all duration-300 ease-out"
                    style={{ width: `${progress}%` }}
                  />
                </div>
                <p className="text-xs text-muted-foreground/70 mt-1.5">
                  This can take 30s–2 minutes to complete
                </p>
              </div>

              {/* Skeleton widget preview — matches SandboxedView dimensions */}
              {(() => {
                const skeletonIsHalf = (selectedSize || existingViewSize) === 'half';
                return (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.97 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: 0.3, duration: 0.4 }}
                    className={`rounded-xl border border-border/50 bg-card overflow-hidden ${
                      skeletonIsHalf ? 'w-[calc(50%-6px)]' : 'w-full'
                    }`}
                    style={{
                      aspectRatio: skeletonIsHalf ? '4 / 3' : '16 / 9',
                      maxHeight: skeletonIsHalf ? '45vh' : '50vh',
                    }}
                  >
                    <div className="w-full h-full flex flex-col">
                      {/* Skeleton title bar */}
                      <div className={`flex items-center gap-2 flex-shrink-0 ${
                        skeletonIsHalf ? 'px-2 pt-2 pb-1 gap-1.5' : 'px-4 pt-3 pb-2 gap-2.5'
                      }`}>
                        <div className={`rounded bg-muted-foreground/10 animate-pulse ${
                          skeletonIsHalf ? 'h-2.5 w-2.5' : 'h-3.5 w-3.5'
                        }`} />
                        <div className={`rounded-md bg-muted-foreground/10 animate-pulse ${
                          skeletonIsHalf ? 'h-2 w-16' : 'h-3 w-28'
                        }`} />
                        <div className={`ml-auto rounded-md bg-muted-foreground/8 animate-pulse ${
                          skeletonIsHalf ? 'h-2 w-8' : 'h-3 w-12'
                        }`} />
                      </div>
                      {/* Skeleton chart area */}
                      <div className={`flex-1 min-h-0 ${
                        skeletonIsHalf ? 'px-2 pb-2' : 'px-4 pb-3'
                      }`}>
                        <div className="relative w-full h-full rounded-lg bg-muted/60 overflow-hidden">
                          {/* Shimmer overlay */}
                          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-muted-foreground/5 to-transparent bg-[length:200%_100%] animate-shimmer" />
                          {/* Fake bar chart columns */}
                          <div className={`absolute left-3 right-3 flex items-end ${
                            skeletonIsHalf ? 'bottom-2 gap-1 h-[60%]' : 'bottom-3 gap-2 h-[70%]'
                          }`}>
                            {(skeletonIsHalf ? [55, 70, 40, 85, 60] : [55, 70, 40, 85, 60, 45, 75]).map((h, i) => (
                              <div
                                key={i}
                                className="flex-1 rounded-t bg-muted-foreground/8 animate-pulse"
                                style={{
                                  height: `${h}%`,
                                  animationDelay: `${i * 150}ms`,
                                }}
                              />
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                );
              })()}
            </motion.div>
          )}

          <div ref={messagesEndRef} />
        </div>

        {/* Accept / Reject bar */}
        {pendingHtml && !accepted && !isLoading && (
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex-shrink-0 border-t pt-3 pb-1 flex items-center justify-center gap-3"
          >
            <Button
              onClick={handleAccept}
              size="sm"
              className="gap-1.5 bg-green-600 hover:bg-green-700 text-white"
              disabled={saveView.isPending || updateView.isPending}
            >
              {saveView.isPending || updateView.isPending ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <Check className="h-3.5 w-3.5" />
              )}
              {isRefineMode ? "Update Widget" : "Pin to Dashboard"}
            </Button>
            <Button
              onClick={handleReject}
              size="sm"
              variant="ghost"
              className="gap-1.5 text-muted-foreground"
            >
              <X className="h-3.5 w-3.5" />
              Reject
            </Button>
          </motion.div>
        )}

        {/* Accepted confirmation */}
        {accepted && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="flex-shrink-0 border-t pt-3 pb-1 text-center"
          >
            <p className="text-sm text-green-500 font-medium flex items-center justify-center gap-1.5">
              <Check className="h-4 w-4" />
              {isRefineMode ? "Widget updated!" : "Pinned to your dashboard!"}
            </p>
          </motion.div>
        )}

        {/* Input Area */}
        <div className="flex-shrink-0 pt-2">
          <div className="flex gap-2">
            <textarea
              ref={inputRef}
              value={inputValue}
              onChange={(e) => {
                setInputValue(e.target.value);
                autoResizeInput();
              }}
              onKeyDown={handleKeyDown}
              placeholder={
                !selectedSize && !isRefineMode
                  ? "Pick a size above to get started..."
                  : pendingHtml
                    ? "Describe changes..."
                    : "Describe the widget you want..."
              }
              disabled={isLoading || (!selectedSize && !isRefineMode)}
              rows={1}
              className="flex-1 rounded-2xl flex w-full border border-input bg-background px-3 py-2 text-base md:text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 resize-none overflow-hidden"
            />
            <Button
              onClick={() => submitMessage(inputValue)}
              disabled={!inputValue.trim() || isLoading || (!selectedSize && !isRefineMode)}
              size="icon"
              className={`h-10 w-10 rounded-full transition-all duration-200 ${
                inputValue.trim() && !isLoading
                  ? "bg-blue-500 hover:bg-blue-600 text-white shadow-md hover:shadow-lg active:scale-95"
                  : "bg-muted text-muted-foreground"
              }`}
            >
              {isLoading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <ArrowUp className="h-5 w-5" />
              )}
            </Button>
          </div>
          <p className="text-xs text-muted-foreground mt-2 text-center">
            3 credits per widget creation
          </p>
          <p className="text-xs text-muted-foreground/60 text-center">
            You have{" "}
            {typeof userCredits === "number"
              ? (userCredits / 10).toFixed(1)
              : "..."}{" "}
            credits
          </p>
        </div>
        </>
      </DialogContent>
    </Dialog>
  );
};
